import { events } from "@/common/constants";
import { wait } from "@/mono/lib/common";
import emitter from "@/mono/lib/emitter";
import { useCSSClassTransition } from "@/mono/lib/hooks/useAnimation";
import useEvent from "@/mono/lib/hooks/useEvent";
import { useIsClient } from "@/mono/lib/hooks/useIsClient";
import { easeOutQuad } from "@/mono/lib/styles/easing";
import { setVh } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";
import { useScrollbar } from "@/mono/third-party/r3f-scroll-rig-custom";
import { useRef, useState, useEffect, useCallback } from "react";
import ReactPlayer from "react-player";
import { getProcessedSrc } from "@/lib/processedSrc";
import styled from "styled-components";
import BorderedIcon from "./BorderedIcon";

export const VIDEO_PLAYER_PLAY = "VideoPlayer:PLAY";
export const VIDEO_PLAYER_STOP = "VideoPlayer:STOP";

const duration = 0.4;

export default function VideoPlayer() {
  const isClient = useIsClient();

  const [src, setSrc] = useState(null);
  const wrapper = useRef();
  const player = useRef();

  const { __lenis: lenis } = useScrollbar();

  const { animateIn, animateOut } = useCSSClassTransition({
    element: wrapper,
  });

  const closeIcon = useRef();

  const playVideo = useCallback(
    ({ src }) => {
      closeIcon.current?.animateIn?.();
      if (wrapper.current) wrapper.current.style.visibility = "visible";
      animateIn();
      useGlobalStore.getState().lenis.stop();
      const noGL = useGlobalStore.getState().noWebGLImages;
      setSrc(noGL ? src : getProcessedSrc(src));

      emitter.emit(events.CURSOR_SHOW, { text: "Click anywhere to close" });
    },
    [animateIn, lenis]
  );

  const stopVideo = useCallback(async () => {
    closeIcon.current?.animateOut?.();
    animateOut();
    emitter.emit(events.CURSOR_HIDE);
    useGlobalStore.getState().lenis.start();
    setSrc(null);

    await wait(duration * 1000);

    if (wrapper.current) wrapper.current.style.visibility = "hidden";
  }, [animateOut, lenis]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        stopVideo();
      }
    },
    [stopVideo]
  );

  const isMobileLayout = useGlobalStore((s) => s.isMobileLayout);

  // useEffect(() => {
  //   if (src) {
  //     document.addEventListener("keydown", handleKeyDown);
  //   } else {
  //     document.removeEventListener("keydown", handleKeyDown);
  //   }

  //   return () => {
  //     document.removeEventListener("keydown", handleKeyDown);
  //   };
  // }, [src, handleKeyDown]);

  useEvent(VIDEO_PLAYER_PLAY, playVideo);
  useEvent(VIDEO_PLAYER_STOP, stopVideo);

  const handleWrapperClick = useCallback(
    (e) => {
      stopVideo();
    },
    [stopVideo]
  );

  if (!isClient) return null;

  return (
    <VideoWrapper ref={wrapper} onClick={handleWrapperClick}>
      <div className="video-inner">
        <BorderedIcon
          ref={closeIcon}
          animateOnScroll={false}
          icon="close"
          onClick={stopVideo}
        />
      </div>
      {src && (
        <ReactPlayer
          controls
          playing
          ref={player}
          url={src}
          config={{
            file: {
              attributes: {
                crossOrigin: "anonymous",
              },
            },
          }}
          className="video-player"
          onPause={() => {
            if (!isMobileLayout) stopVideo();
          }}
          onEnded={async () => {
            await wait(400);

            stopVideo();
          }}
          stopOnUnmount={true}
        />
      )}
    </VideoWrapper>
  );
}

const VideoWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  /* display: none; */

  width: 100vw;
  height: ${setVh(100)};

  z-index: 1000;
  background-color: rgba(0, 0, 0, 1);

  justify-content: center;
  align-items: center;

  visibility: hidden;

  transition: opacity ${duration}s ${easeOutQuad};
  opacity: 0;

  visibility: hidden;

  &.active {
    opacity: 1;
  }

  .video-player {
    width: 100% !important;
    height: 100% !important;
    pointer-events: none;
  }

  .video-inner {
    width: 100%;
    height: 100%;
    position: absolute;
  }

  .icon-container {
    position: absolute;
    top: ${(props) => props.theme.misc.basePadding()};
    right: ${(props) => props.theme.misc.basePadding()};

    .icon-wrapper {
      color: ${({ theme }) => theme.theme.menuText} !important;
      border-color: ${({ theme }) => theme.theme.menuText} !important;
    }
    .icon-background {
      background-color: ${({ theme }) => theme.theme.menuText} !important;
    }

    path {
      fill: ${({ theme }) => theme.theme.menuText} !important;
      stroke: ${({ theme }) => theme.theme.menuText} !important;
    }

    .icon-duplicate {
      fill: ${({ theme }) => theme.theme.menuBackground} !important;

      path {
        fill: ${({ theme }) => theme.theme.menuBackground} !important;
        stroke: ${({ theme }) => theme.theme.menuBackground} !important;
      }
    }
  }
`;
